import { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { navLinks, hotTopics, siteInfo } from "@/mocks/site";

export default function SiteHeader() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  const handleSearch = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const q = keyword.trim();
    setSearchOpen(false);
    setMenuOpen(false);
    navigate(q ? `/materials?q=${encodeURIComponent(q)}` : "/materials");
  };

  const today = new Date().toLocaleDateString("zh-CN", {
    year: "numeric",
    month: "long",
    day: "numeric",
    weekday: "long",
  });

  return (
    <header className="w-full">
      <div className="bg-foreground-950 text-background-100">
        <div className="mx-auto flex h-9 w-full max-w-[1400px] items-center gap-4 px-4 md:px-8">
          <span className="hidden whitespace-nowrap text-xs text-background-300 md:block">{today}</span>
          <span className="hidden h-3 w-px bg-background-700 md:block" />
          <div className="relative flex-1 overflow-hidden">
            <div className="flex w-max animate-marquee items-center gap-8 whitespace-nowrap">
              {[...hotTopics, ...hotTopics].map((topic, index) => (
                <span key={`${topic}-${index}`} className="flex items-center gap-2 text-xs text-background-200">
                  <i className="ri-fire-fill text-primary-400" />
                  {topic}
                </span>
              ))}
            </div>
          </div>
          <span className="hidden whitespace-nowrap text-xs text-background-300 lg:block">
            {siteInfo.serviceHours}
          </span>
        </div>
      </div>

      <div className="sticky top-0 z-50 border-b border-background-200 bg-background-50/90 backdrop-blur">
        <div className="mx-auto flex h-16 w-full max-w-[1400px] items-center justify-between gap-4 px-4 md:px-8">
          <Link to="/" className="flex items-center gap-2.5" aria-label={`${siteInfo.brand} 首页`}>
            <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-500 font-heading text-lg font-bold text-background-50">
              料
            </span>
            <span className="flex flex-col leading-none">
              <span className="font-heading text-xl font-bold tracking-tight text-foreground-950">
                {siteInfo.brand}
              </span>
              <span className="mt-0.5 text-[11px] tracking-wide text-foreground-500">{siteInfo.slogan}</span>
            </span>
          </Link>

          <nav className="hidden items-center gap-1 md:flex" aria-label="主导航">
            {navLinks.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                end={link.to === "/"}
                className={({ isActive }) =>
                  `whitespace-nowrap rounded-md px-4 py-2 text-sm font-medium transition-colors ${
                    isActive
                      ? "bg-primary-100 text-primary-700"
                      : "text-foreground-700 hover:bg-background-100 hover:text-foreground-950"
                  }`
                }
              >
                {link.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setSearchOpen((prev) => !prev)}
              className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-md text-foreground-700 transition-colors hover:bg-background-100 hover:text-primary-600"
              aria-label="搜索话题"
            >
              <i className="ri-search-line text-lg" />
            </button>
            <Link
              to="/materials"
              className="hidden whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 md:inline-flex"
            >
              今日猛料
            </Link>
            <button
              type="button"
              onClick={() => setMenuOpen((prev) => !prev)}
              className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-md text-foreground-800 transition-colors hover:bg-background-100 md:hidden"
              aria-label="打开菜单"
              aria-expanded={menuOpen}
            >
              <i className={menuOpen ? "ri-close-line text-xl" : "ri-menu-line text-xl"} />
            </button>
          </div>
        </div>

        {searchOpen && (
          <div className="border-t border-background-200 bg-background-100">
            <form
              onSubmit={handleSearch}
              className="mx-auto flex w-full max-w-[1400px] items-center gap-3 px-4 py-3 md:px-8"
              role="search"
            >
              <i className="ri-search-line text-lg text-foreground-500" />
              <input
                type="search"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="输入你想找的话题关键词，例如：综艺、悬疑、音乐节"
                className="h-10 flex-1 rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-900 outline-none placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                autoFocus
              />
              <button
                type="submit"
                className="whitespace-nowrap rounded-md bg-foreground-950 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-foreground-800"
              >
                搜索
              </button>
            </form>
          </div>
        )}
      </div>

      {menuOpen && (
        <div className="border-b border-background-200 bg-background-50 md:hidden">
          <form onSubmit={handleSearch} className="flex items-center gap-2 px-4 py-3" role="search">
            <input
              type="search"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索话题关键词"
              className="h-10 flex-1 rounded-md border border-background-300 bg-background-100 px-3 text-sm text-foreground-900 outline-none placeholder:text-foreground-400 focus:border-primary-400"
            />
            <button
              type="submit"
              className="flex h-10 w-10 items-center justify-center rounded-md bg-primary-500 text-background-50"
              aria-label="提交搜索"
            >
              <i className="ri-search-line" />
            </button>
          </form>
          <nav className="flex flex-col px-4 pb-4" aria-label="移动端导航">
            {navLinks.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                end={link.to === "/"}
                onClick={() => setMenuOpen(false)}
                className={({ isActive }) =>
                  `border-b border-background-200 py-3 text-base font-medium ${
                    isActive ? "text-primary-600" : "text-foreground-800"
                  }`
                }
              >
                {link.label}
              </NavLink>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}